import { Link, useParams } from 'wouter';
import React from 'react';
import { posts } from './BlogPage';

const logoHeader = '/assets/ALWAYS_logo_Web_RGB_POS.png';
const logoFooter = '/assets/ALWAYS_logo_Web_RGB_NEG.png';

export default function BlogPost() {
  const params = useParams();
  const post = posts.find(p => p.slug === params.slug);

  // Scroll to top when component mounts or slug changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [params.slug]);

  if (!post) {
    return (
      <div className="bg-slate-50 text-slate-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Blog post not found</h1>
          <Link href="/blog">
            <a className="text-slate-900 font-semibold hover:underline">← Back to Blog</a>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href="/" data-testid="link-home">
            <a className="flex items-center gap-2">
              <img src={logoHeader} alt="Allwayswithyou logo" className="h-8 w-auto" />
            </a>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="/#value" className="hover:underline">Why</a>
            <a href="/#centre" className="hover:underline">Care Plan</a>
            <a href="/#pilot-programme" className="hover:underline">Pilot Programme</a>
            <Link href="/blog" data-testid="link-blog">
              <a className="hover:underline">Blog/News</a>
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href="https://cpm.allwayswithyou.com" target="_blank" rel="noopener noreferrer" data-testid="button-cpm-login" className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">CPM Login</a>
          </div>
        </div>
      </header>

      {/* Back to Blog Link */}
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <Link href="/blog" data-testid="link-back-to-blog">
          <a className="text-slate-600 hover:text-slate-900 font-medium">← Back to Blog</a>
        </Link>
      </div>

      {/* Blog Post Content */}
      <section className="py-8 md:py-12 pb-20">
        <div className="max-w-4xl mx-auto px-4">
          <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                {post.category}
              </span>
              <span className="text-slate-500 text-sm">{post.date}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">{post.title}</h1>
            <p className="text-xl text-slate-600 mb-8">{post.excerpt}</p>
            <div className="prose prose-slate max-w-none">
              {post.content?.split('\n\n').map((paragraph, idx) => {
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return (
                    <h3 key={idx} className="text-xl font-bold mt-8 mb-4 text-slate-900">
                      {paragraph.replace(/\*\*/g, '')}
                    </h3>
                  );
                }
                return (
                  <p key={idx} className="text-slate-700 mb-4 leading-relaxed">
                    {paragraph}
                  </p>
                );
              })}
            </div>
            <div className="mt-8 pt-8 border-t border-slate-200">
              <p className="text-slate-600">
                Learn more at <Link href="/" className="text-slate-900 font-semibold hover:underline">www.allwayswithyou.com</Link>
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Footer */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-extrabold">Ready to see it in your workflow?</h2>
          <p className="mt-3 text-slate-300 max-w-2xl mx-auto">Start with a pilot site or roll out across branches. We'll map your assessments and approvals to Allwayswithyou.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="mailto:info@allwayswithyou.com" data-testid="button-schedule-demo" className="px-5 py-3 rounded-xl bg-white text-slate-900 font-semibold hover:bg-slate-100">Schedule Demo</a>
            <a href="mailto:info@allwayswithyou.com" data-testid="button-get-quote" className="px-5 py-3 rounded-xl bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20">Get a Tailored Quote</a>
          </div>
          <div className="mt-12 text-left bg-white/5 rounded-2xl p-6 md:p-8 mx-auto max-w-4xl">
            <h3 className="font-semibold mb-2">About Allwayswithyou</h3>
            <p className="text-slate-200">
              Allwayswithyou is a digital platform that transforms how healthcare providers create, manage, and communicate care plans — across homecare, cancer care, disability, and community health services. By automating care plan generation from standard assessments, it delivers consistent, compliant documentation in minutes rather than hours. Role-based insights and collaboration tools connect assessors, clinicians, and families in real time, ensuring clarity and accountability at every stage of care. Designed as a secure, configurable SaaS platform, Allwayswithyou helps organisations raise quality, reduce administrative burden, and strengthen trust between patients, families, and multidisciplinary care teams.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-4 mt-10">
            <img src={logoFooter} alt="Allwayswithyou full logo" className="w-40 h-auto" />
            <nav className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-slate-400">
              <Link href="/privacy-policy" className="hover:text-white transition-colors" data-testid="link-privacy-policy">Privacy Policy</Link>
              <Link href="/terms-conditions" className="hover:text-white transition-colors" data-testid="link-terms-conditions">Terms &amp; Conditions</Link>
              <Link href="/cookie-policy" className="hover:text-white transition-colors" data-testid="link-cookie-policy">Cookie Policy</Link>
              <Link href="/company-info" className="hover:text-white transition-colors" data-testid="link-company-info">Company Information</Link>
            </nav>
            <p className="text-slate-400 text-sm">© 2025 Allwayswithyou. All rights reserved.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
